import { OUTPUT_PRESETS, projectSlideCount } from './project.js';

export const RENDER_CONTRACT_VERSION = 1;
export const EXPORT_FPS = 24;

export const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, Number(value) || 0));
export const ease = (value) => -(Math.cos(Math.PI * clamp(value)) - 1) / 2;

export function inlineStyledRuns(value) {
  const text = String(value ?? '');
  const runs = [];
  let buffer = '', bold = false, power = false;
  const flush = () => {
    if (!buffer) return;
    const previous = runs.at(-1);
    if (previous && previous.bold === bold && previous.power === power) previous.text += buffer;
    else runs.push({ text:buffer, bold, power });
    buffer = '';
  };
  for (let index = 0; index < text.length;) {
    const marker = text.slice(index, index + 2);
    if (marker === '**' && (bold || text.indexOf('**', index + 2) >= 0)) { flush(); bold = !bold; index += 2; continue; }
    if (marker === '[[' && text.indexOf(']]', index + 2) >= 0) { flush(); power = true; index += 2; continue; }
    if (marker === ']]' && power) { flush(); power = false; index += 2; continue; }
    buffer += text[index++];
  }
  flush();
  return runs.length ? runs : [{ text:'', bold:false, power:false }];
}

export function inlineTextRuns(value) {
  const runs = [];
  for (const run of inlineStyledRuns(value)) {
    const previous = runs.at(-1);
    if (previous && previous.bold === run.bold) previous.text += run.text;
    else runs.push({ text:run.text, bold:run.bold });
  }
  return runs;
}

export function plainInlineText(value) {
  return inlineTextRuns(value).map(run => run.text).join('');
}

export function motionScale(mode, strength, progress) {
  const amount = Math.max(1, Number(strength) || 1);
  const eased = ease(progress);
  if (mode === 'zoom_in') return 1 + (amount - 1) * eased;
  if (mode === 'zoom_out') return amount - (amount - 1) * eased;
  if (mode === 'zoom_in_out') return 1 + (amount - 1) * (1 - Math.cos(Math.PI * 2 * eased)) / 2;
  return 1;
}

export function animationState(kind, time, duration) {
  const progress = duration > 0 ? clamp(time / duration) : 1;
  if (kind === 'fade') return { alpha: ease(progress), y: 0, scale: 1 };
  if (kind === 'slide_up') return { alpha: ease(progress), y: (1 - ease(progress)) * 34, scale: 1 };
  if (kind === 'pop') return { alpha: ease(progress), y: 0, scale: .78 + .22 * ease(progress) };
  return { alpha: 1, y: 0, scale: 1 };
}

export function resolveTimeline(absoluteTime, count, duration) {
  const safeCount = Math.max(1, Number(count) || 0);
  const safeDuration = Math.max(.001, Number(duration) || 5);
  const total = safeCount * safeDuration;
  const time = Math.min(Math.max(0, Number(absoluteTime) || 0), Math.max(0, total - .0001));
  const slideIndex = Math.min(safeCount - 1, Math.floor(time / safeDuration));
  return { slideIndex, slideTime: time - slideIndex * safeDuration, total };
}

export function describeExportWorkload(project, quality = 'hd', fps = EXPORT_FPS) {
  const preset = OUTPUT_PRESETS[quality] || OUTPUT_PRESETS.hd;
  const count = Math.max(1, projectSlideCount(project));
  const duration = Math.max(.001, Number(project?.settings?.duration) || 5);
  const totalFrames = Math.ceil(count * duration * fps);
  return {
    contractVersion: RENDER_CONTRACT_VERSION,
    quality: OUTPUT_PRESETS[quality] ? quality : 'hd',
    width: preset.width,
    height: preset.height,
    fps,
    slideCount: count,
    durationSeconds: count * duration,
    totalFrames,
    pixelFrames: preset.width * preset.height * totalFrames,
  };
}

export function rendererRegressionSnapshot(settings) {
  return [0, .25, .5, .75, 1].map(progress => ({
    progress,
    scale: Number(motionScale(settings.motion, settings.zoom, progress).toFixed(6)),
    cover: Object.fromEntries(Object.entries(animationState(settings.coverTextAnimation, progress * settings.coverTextAnimationDuration, settings.coverTextAnimationDuration)).map(([key,value]) => [key, Number(value.toFixed(6))])),
  }));
}
